import * as React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS, THUMB_FOCUS_GRADIENT } from '@/constants';

import {
  InfoAside,
  WarningAside,
  SuccessAside,
} from '@/components/Aside';

// I'm defining my own `onChange` prop which does *not* work like the one built into inputs. I need to omit it from `InputHTMLAttributes`, and provide my own below.
type InputWithoutOnChange = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange'
>;

export interface Props extends InputWithoutOnChange {
  value: number;
  onChange?: (value: number) => void;
  handleSize?: number;
  trackSize?: number;
  backgroundColor?: string;
  handleColor?: string;
  handleFocusColor?: string;
}

const Slider = React.forwardRef(function Slider(
  {
    value,
    onChange,
    handleSize = 16,
    trackSize = 3,
    backgroundColor = 'var(--color-background)',
    handleColor = 'var(--color-gray-900)',
    handleFocusColor = THUMB_FOCUS_GRADIENT,
    style = {},
    ...delegated
  }: Props,
  ref: React.Ref<HTMLInputElement>
) {
  return (
    <Input
      ref={ref}
      style={{
        ...style,
        '--handle-color': handleColor,
        '--handle-focus-color': handleFocusColor,
        '--background-color': backgroundColor,
        '--base-handle-size': handleSize + 'px',
        '--track-size': trackSize + 'px',
      }}
      type="range"
      value={value}
      onChange={(ev) => {
        if (typeof onChange === 'function') {
          onChange(Number(ev.target.value));
        }
      }}
      {...delegated}
    />
  );
});

const Input = styled.input`
  --handle-size: var(--base-handle-size);

  display: block;
  width: 100%;
  background: transparent;
  appearance: none;
  outline-offset: 6px;
  outline-color: var(--color-text);
  cursor: pointer;
  /* On Android Firefox, the page can still be scrolled while dragging a horizontal slider. Maybe this will fix it? */
  touch-action: none;

  @media ${BREAKPOINTS.smAndSmaller} {
    --handle-size: calc(var(--base-handle-size) * 1.5);
  }

  &::-webkit-slider-thumb {
    appearance: none;
    height: var(--handle-size);
    width: var(--handle-size);
    border-radius: 50px;
    background: var(--handle-color);
    cursor: grab;
    transform: translateY(calc(-50% + (var(--track-size) / 2)));
    outline: 2px solid var(--background-color);

    &:active {
      cursor: grabbing;
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;

    &::-webkit-slider-thumb {
      cursor: not-allowed;

      &:active {
        cursor: not-allowed;
      }
    }
  }

  &:focus::-webkit-slider-thumb {
    background: var(--handle-focus-color);
  }

  /* In Light Mode, the golden gradient doesn't have enough contrast. */
  html[data-color-mode='light'] &:focus::-webkit-slider-thumb {
    background: var(--handle-color);
  }

  &::-webkit-slider-runnable-track {
    width: calc(100% - var(--handle-size));
    height: var(--track-size);
    background-color: var(--color-gray-300);
    border-radius: calc(var(--track-size) / 2);
    margin: calc(var(--handle-size) / 2);
    margin-left: 0;
    margin-right: 0;

    ${InfoAside} & {
      background-color: var(--color-info-400);
    }
    ${WarningAside} & {
      background-color: var(--color-warning-400);
    }
    ${SuccessAside} & {
      background-color: var(--color-success-400);
    }
  }

  &::-moz-range-track {
    width: calc(100% - var(--handle-size));
    height: var(--track-size);
    background-color: var(--color-gray-300);
    border-radius: calc(var(--track-size) / 2);
    margin: calc(var(--handle-size) / 2);
    margin-left: 0;
    margin-right: 0;
  }
`;

export default Slider;
